import { Injectable } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { BehaviorSubject } from 'rxjs';

import { RuleExpression } from '../../rule-config-component/models/rule-engine';
import { isDuplicateRuleName, convertNamedRuleExpressionToNumbers } from '../../services/rule-engine.service';
import type {
  RoutingPipelineBlock,
  RoutingRuleconfig1DialogData,
  RoutingVersionOption,
} from '../models/routing-ruleconfig1.models';

/** Shared dialog state for routing-ruleconfig1 (XML and HL7). */
@Injectable()
export class RoutingRuleconfig1StoreService {
  dialogData: RoutingRuleconfig1DialogData = {};
  conversionType: 'xmltojson' | 'hl7tojson' = 'xmltojson';
  uniqueID = '';
  ruleExecutionStage = '';
  routingAgentId = '';
  routeName = '';
  sourceIsTestOrganization = '';
  destinationIsTestOrganization = '';
  testOrgFlagsLoaded = false;
  pipelineBlocks: RoutingPipelineBlock[] = [];

  get isHl7(): boolean {
    return this.conversionType === 'hl7tojson';
  }

  /** Pipeline id → display label (search / API). */
  readonly pipelineIdToDisplayName = new Map<string, string>();
  selectedPipelineId: string | null = null;
  editingBlockId: string | null = null;
  editingRuleIndex: number | null = null;
  isPipelineLocked = false;

  versionOptions: RoutingVersionOption[] = [];
  selectedVersion: string | null = null;
  pagerolepermisions: Record<string, boolean> = {};
  isSendToQc = false;
  isApproveReject = false;
  showCompareOverlay = false;
  showVersionNotes = false;
  showQcNote = false;
  notesLoading = false;
  qcNoteContent = '';
  qcStage: 'qc' | 'approve' | 'reject' | null = null;
  qcAudit: { role: string; notes: string; user: string; timestamp: string }[] = [];
  /** Last saved rule config payload (used by Send to QC / Approve). */
  latestRuleExpressions: unknown[] | null = null;
  compareLeftVersion: string | null = null;
  compareRightVersion: string | null = null;
  compareDiffOldModel = { code: '', language: 'json' };
  compareDiffNewModel = { code: '', language: 'json' };
  compareDiffOptions = {
    theme: 'vs',
    language: 'json',
    readOnly: true,
    renderSideBySide: true,
    lineNumbers: true,
    wordWrap: 'on',
    scrollBeyondLastLine: false,
  };

  /** JSON label → persisted xpath (Build Path / pick). */
  readonly fieldDisplayToPath = new Map<string, string>();

  readonly ruleForm = new FormGroup({
    pipeline: new FormControl('', Validators.required),
    rulename: new FormControl('', Validators.required),
    field: new FormControl('', Validators.required),
    operator: new FormControl('', Validators.required),
    value: new FormControl('', Validators.required),
    hl7Segment: new FormControl(''),
    hl7FieldIndex: new FormControl<string | null>(null),
    hl7ComponentIndex: new FormControl<string | null>(null),
    hl7SubComponentIndex: new FormControl<string | null>(null),
  });

  private readonly blocks$ = new BehaviorSubject<RoutingPipelineBlock[]>([]);
  readonly pipelineBlocks$ = this.blocks$.asObservable();

  get fieldControl(): FormControl {
    return this.ruleForm.get('field') as FormControl;
  }

  get selectedPipelineBlock(): RoutingPipelineBlock | null {
    const id = (this.selectedPipelineId ?? '').trim();
    if (!id) {
      return null;
    }
    return this.pipelineBlocks.find((b) => b.id === id) ?? null;
  }

  isSelectedPipelineInBlocks(): boolean {
    const id = (this.selectedPipelineId ?? '').trim();
    if (!id) {
      return false;
    }
    return this.pipelineBlocks.some((b) => b.id === id);
  }

  patchBlocks(blocks: RoutingPipelineBlock[]): void {
    this.pipelineBlocks = blocks;
    this.blocks$.next(blocks);
  }

  isEditingAnyRule(): boolean {
    return this.editingBlockId !== null && this.editingRuleIndex !== null;
  }

  get selectedVersionStatus(): string {
    const opt = this.versionOptions.find((v) => v.value === this.selectedVersion);
    return (opt?.status ?? '').toString().trim().toLowerCase();
  }

  get selectedVersionDisplay(): string {
    if (this.selectedVersion == null || !this.versionOptions.length) {
      return '';
    }
    const option = this.versionOptions.find((o) => o.value?.toString() === this.selectedVersion?.toString());
    if (!option) {
      return `Version ${this.selectedVersion}`;
    }
    const parts = [option.value];
    if (option.status) {
      parts.push(option.status);
    }
    if (option.created) {
      parts.push(`(${option.created})`);
    }
    return parts.join(' - ');
  }

  get qcActionLabel(): string {
    switch (this.qcStage) {
      case 'qc':
        return 'Send to QC';
      case 'approve':
        return 'Approve';
      case 'reject':
        return 'Reject';
      default:
        return '';
    }
  }

  hasAnyRules(): boolean {
    return (this.pipelineBlocks || []).some((b) => (b.rules?.length ?? 0) > 0);
  }

  get bothEntitiesAreTestOrganization(): boolean {
    return this.testOrgFlagsLoaded
      && this.sourceIsTestOrganization === '1'
      && this.destinationIsTestOrganization === '1';
  }

  mapOperatorToUi(op: unknown): string {
    const v = `${op ?? ''}`.trim().toUpperCase();
    if (v === 'IN') return 'IN';
    if (v === 'NOT IN' || v === 'NOTIN') return 'NOT IN';
    if (v === 'CONTAINS') return 'CONTAINS';
    if (v === 'CUSTOM') return 'Custom';
    return `${op ?? ''}`.toString().trim() || 'IN';
  }

  getRuleNameFromRule(rule: RuleExpression, index: number): string {
    const name = (rule.rulename ?? '').toString().trim();
    return name || `Rule${index || rule.rulenumber || 1}`;
  }

  getRuleNameInputValue(): string {
    const control = this.ruleForm.get('rulename');
    if (!control) {
      return '';
    }
    const raw = control.disabled ? control.getRawValue() : control.value;
    return (raw ?? '').toString().trim();
  }

  isDuplicateRuleNameInBlock(
    block: RoutingPipelineBlock,
    ruleName: string,
    excludeRuleIndex: number | null
  ): boolean {
    return isDuplicateRuleName(block.rules ?? [], ruleName, { excludeRuleIndex });
  }

  setRuleNameFieldDisabled(disabled: boolean): void {
    const control = this.ruleForm.get('rulename');
    if (!control) {
      return;
    }
    if (disabled) {
      control.disable({ emitEvent: false });
    } else {
      control.enable({ emitEvent: false });
    }
  }

  buildPersistedPayload(): unknown[] {
    return (this.pipelineBlocks || []).map((block) => ({
      rules: (block.rules || []).map((rule) => this.serializeRuleForSave(rule)),
      // UI may show "test" or "Rule1"; API always gets "2" or "1"
      ruleSatisfaction: convertNamedRuleExpressionToNumbers(
        block.ruleSatisfaction || '',
        block.rules || []
      ),
      pipeline: block.id,
    }));
  }

  private serializeRuleForSave(rule: RuleExpression): Record<string, unknown> {
    const op = this.mapOperatorToUi(rule.operation ?? (rule as { operator?: string }).operator);
    const apiOp =
      (
        {
          IN: 'in',
          'NOT IN': 'notin',
          CONTAINS: 'contains',
        } as Record<string, string>
      )[op.toUpperCase()] ?? op.toLowerCase().split(' ').join('');
    return {
      rulenumber: rule.rulenumber,
      rulename: rule.rulename ?? '',
      field: rule.field ?? '',
      operator: apiOp,
      delimiter: rule.delimiter ?? '|',
      value: rule.value ?? '',
      ruletype: rule.ruletype ?? '',
      param: rule.param ?? '',
    };
  }
}
